import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography, Space, Avatar, Button, Card, Row, Col, Input, Select, Table, Tabs, Form, Switch, Modal, message, Spin } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import UserProfile from './UserProfile';
import DashboardSection from './DashboardSection';
import { settingsAPI } from '../services/api';
import {
  DashboardOutlined, UserOutlined, StarOutlined, RiseOutlined, SettingOutlined,
  ExclamationCircleOutlined, MenuOutlined, BellOutlined, UserAddOutlined,
  EditOutlined, DeleteOutlined, GiftOutlined, LockOutlined,
  TeamOutlined, SecurityScanOutlined,
  ApiOutlined, GlobalOutlined, CalculatorOutlined, MailOutlined
} from '@ant-design/icons';
import './Settings.css';
import ApiRateLimiting from './ApiRateLimiting';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const Settings: React.FC = () => {
  const isSuperAdmin = localStorage.getItem('admin_is_super_admin') === 'true';
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showApiRateLimiting, setShowApiRateLimiting] = useState(false);
  const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);
  const [isEditUserModalVisible, setIsEditUserModalVisible] = useState(false);
  const [isDeleteUserModalVisible, setIsDeleteUserModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [deletingUser, setDeletingUser] = useState<any>(null);
  const [settingsData, setSettingsData] = useState<any>(null);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addUserForm] = Form.useForm();
  const [editUserForm] = Form.useForm();
  const [profileForm] = Form.useForm();
  const [personalProfile, setPersonalProfile] = useState<any>({
    name: '',
    email: '',
    phone: '',
    role: '',
    notifications: {
      email: true,
      push: true,
      sms: false
    }
  });
  const navigate = useNavigate();
  const location = useLocation();

  const getCurrentAdminEmail = (): string | null => {
    const cachedEmail = localStorage.getItem('admin_email');
    if (cachedEmail) return cachedEmail.toLowerCase();
    const storedUsername = localStorage.getItem('admin_username');
    if (storedUsername?.includes('@')) return storedUsername.toLowerCase();
    const matched = teamMembers.find(
      (m: any) => m?.email === storedUsername || m?.name === storedUsername
    );
    return matched?.email?.toLowerCase() || personalProfile?.email?.toLowerCase() || null;
  };

  const isCurrentUser = (record: any): boolean => {
    const currentEmail = getCurrentAdminEmail();
    return !!currentEmail && record?.email?.toLowerCase() === currentEmail;
  };

  // Load settings data from API
  const loadSettingsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [settingsResponse, teamResponse] = await Promise.all([
        settingsAPI.getSettings(),
        settingsAPI.getTeamMembers()
      ]);
      
      if (settingsResponse.success) {
        setSettingsData(settingsResponse.data);
        // Update personal profile with loaded data
        if (settingsResponse.data) {
          if (settingsResponse.data.email) {
            localStorage.setItem('admin_email', settingsResponse.data.email);
          }
          setPersonalProfile((prev: any) => ({
            ...prev,
            ...settingsResponse.data,
            notifications: settingsResponse.data.notifications || {
              email: true,
              push: true,
              sms: false
            }
          }));
        }
      } else {
        setError('Failed to load settings');
        setSettingsData(null);
      }
      
      if (teamResponse.success) {
        setTeamMembers(teamResponse.data);
        const cachedEmail = localStorage.getItem('admin_email');
        const storedUsername = localStorage.getItem('admin_username');
        let matchedMember: any = null;
        if (cachedEmail && teamResponse.data?.length) {
          matchedMember = teamResponse.data.find((m: any) => m?.email === cachedEmail);
        }
        if (!matchedMember && storedUsername && teamResponse.data?.length) {
          matchedMember = teamResponse.data.find((m: any) =>
            m?.email === storedUsername || m?.name === storedUsername
          );
          if (matchedMember?.email) {
            localStorage.setItem('admin_email', matchedMember.email);
          }
        }
        // Prefill personal profile from current team member (#61)
        if (matchedMember) {
          setPersonalProfile((prev: any) => ({
            ...prev,
            name: matchedMember.name || prev.name,
            email: matchedMember.email || prev.email,
            role: matchedMember.role || prev.role,
            phone: matchedMember.phone || prev.phone,
            notifications: settingsResponse?.success && settingsResponse?.data?.notifications
              ? settingsResponse.data.notifications
              : (prev.notifications || { email: true, push: true, sms: false })
          }));
        }
      } else {
        setError('Failed to load team members');
        setTeamMembers([]);
      }
      
    } catch (error) {
      console.error('Error loading settings data:', error);
      setError('Failed to load settings data');
      setSettingsData(null);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };


  // Load data on component mount
  useEffect(() => {
    loadSettingsData();
  }, []);

  useEffect(() => {
    if (editingUser && isEditUserModalVisible) {
      editUserForm.setFieldsValue({
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role,
        status: editingUser.status
      });
    }
  }, [editingUser, isEditUserModalVisible, editUserForm]);

  // Prefill Personal Info form when data loads (#61)
  useEffect(() => {
    if (personalProfile && (personalProfile.name || personalProfile.email)) {
      profileForm.setFieldsValue({
        name: personalProfile.name || '',
        email: personalProfile.email || '',
        phone: personalProfile.phone || '',
        role: personalProfile.role || ''
      });
    }
  }, [personalProfile, profileForm]);

  const teamColumns = [
    {
      title: 'Team Member',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <Space>
          <Avatar size={32} style={{ backgroundColor: '#DB8633' }}>
            {record.avatar}
          </Avatar>
          <div>
            <Text strong>{text}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.email}</Text>
          </div>
        </Space>
      ),
      width: 250,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <span className={`role-badge role-${role.toLowerCase().replace(' ', '-')}`}>
          {role}
        </span>
      ),
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <span className={`status-badge status-${status.toLowerCase()}`}>
          {status}
        </span>
      ),
      width: 120,
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (text: string) => <Text type="secondary">{text}</Text>,
      width: 180,
    },
    ...(isSuperAdmin ? [{
      title: 'Actions',
      key: 'actions',
      render: (_text: string, record: any) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditUser(record)}
            className="edit-user-btn"
            title="Edit team member"
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteUser(record)}
            className="delete-user-btn"
            danger
            disabled={isCurrentUser(record)}
            title={isCurrentUser(record) ? 'You cannot delete your own account' : 'Delete team member'}
          />
        </Space>
      ),
      width: 140,
    }] : []),
  ];

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setIsEditUserModalVisible(true);
  };

  const handleDeleteUser = (user: any) => {
    if (isCurrentUser(user)) {
      message.error('You cannot delete your own account.');
      return;
    }
    setDeletingUser(user);
    setIsDeleteUserModalVisible(true);
  };

  const confirmDeleteUser = async () => {
    if (!deletingUser?.id) {
      message.error('Cannot delete team member: missing ID');
      return;
    }
    if (isCurrentUser(deletingUser)) {
      message.error('You cannot delete your own account.');
      return;
    }

    try {
      setLoading(true);
      const response = await settingsAPI.deleteTeamMember(deletingUser.id);
      if (response.success) {
        message.success(`Team member ${deletingUser.name || deletingUser.email} deleted successfully`);
        setIsDeleteUserModalVisible(false);
        setDeletingUser(null);
        await loadSettingsData();
      } else {
        message.error(response.error || response.message || 'Failed to delete team member');
      }
    } catch (error: any) {
      console.error('Error deleting team member:', error);
      message.error(error.message || 'Failed to delete team member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    addUserForm.resetFields();
    setIsAddUserModalVisible(true);
  };

  const handleAddUserSubmit = async (values: any) => {
    try {
      const response = await settingsAPI.addTeamMember(values);
      if (response.success) {
        message.success('Team member added successfully!');
        setIsAddUserModalVisible(false);
        addUserForm.resetFields();
        loadSettingsData();
      } else {
        message.error(response.error || 'Failed to add team member');
      }
    } catch (error: any) {
      console.error('Error adding team member:', error);
      message.error(error.message || 'Failed to add team member. Please try again.');
    }
  };

  const handleEditUserSubmit = async (values: any) => {
    if (!editingUser?.id) {
      message.error('Missing team member ID.');
      return;
    }

    try {
      const response = await settingsAPI.updateTeamMember(editingUser.id, values);
      if (response.success) {
        message.success('Team member updated successfully!');
        setIsEditUserModalVisible(false);
        setEditingUser(null);
        loadSettingsData();
      } else {
        message.error(response.error || 'Failed to update team member');
      }
    } catch (error: any) {
      console.error('Error updating team member:', error);
      message.error(error.message || 'Failed to update team member. Please try again.');
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const handleProfileUpdate = async (values: any) => {
    try {
      const response = await settingsAPI.updateSettings(values);
      
      if (response.success) {
        setPersonalProfile((prev: any) => ({ ...prev, ...values }));
        message.success('Profile updated successfully!');
      } else {
        message.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Failed to update profile. Please try again.');
    }
  };

  const handlePasswordChange = async (values: any) => {
    try {
      const storedUsername = localStorage.getItem('admin_username');
      const matchedEmail = teamMembers?.find((member: any) =>
        member?.email === storedUsername || member?.name === storedUsername
      )?.email;
      let email = localStorage.getItem('admin_email')
        || (storedUsername && storedUsername.includes('@') ? storedUsername : null)
        || matchedEmail
        || personalProfile?.email
        || settingsData?.email;

      if (!email) {
        try {
          const teamResponse = await settingsAPI.getTeamMembers();
          if (teamResponse.success) {
            const fallbackEmail = teamResponse.data?.find((member: any) =>
              member?.email === storedUsername || member?.name === storedUsername
            )?.email;
            if (fallbackEmail) {
              localStorage.setItem('admin_email', fallbackEmail);
              email = fallbackEmail;
            }
          }
        } catch (error) {
          console.error('Error fetching team members for email fallback:', error);
        }
      }

      if (!email) {
        message.error('Missing admin email. Please log in again.');
        return;
      }

      const response = await settingsAPI.changeTeamMemberPassword({
        email,
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });
      
      if (response.success) {
        message.success('Password changed successfully!');
      } else {
        message.error('Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      message.error('Failed to change password. Please try again.');
    }
  };

  const handleNotificationChange = async (key: string, checked: boolean) => {
    const nextNotifications = {
      ...(personalProfile?.notifications || { email: true, push: true, sms: false }),
      [key]: checked
    };
    setPersonalProfile((prev: any) => ({
      ...prev,
      notifications: nextNotifications
    }));
    try {
      await settingsAPI.updateSettings({ notifications: nextNotifications });
      message.success('Notification settings updated!');
    } catch (err) {
      message.error('Failed to save notification preferences');
    }
  };

  return (
    <Layout className="settings-layout">
      <AdminSidebar
        activeKey="settings"
        mobileVisible={mobileSidebarVisible}
        onMobileToggle={() => setMobileSidebarVisible(!mobileSidebarVisible)}
      />

      {/* Main Content */}
      <Layout className="main-content">
        <Header className="settings-header">
          <div className="header-left">
            <Title level={2} style={{ margin: 0 }}>Settings</Title>
            <Text type="secondary" className="settings-subtitle">Manage your account and team</Text>
          </div>
        </Header>

        <Content className="settings-content">
          <Spin spinning={loading}>
            <div className="content-wrapper">
              <DashboardSection
                title="Settings"
                subtitle="Profile, security, team, and API rate-limiting configuration"
                icon={<SettingOutlined />}
              >
              <Tabs
                activeKey={activeTab}
              onChange={handleTabChange}
              className="settings-tabs"
              items={[
                {
                  key: 'profile',
                  label: (
                    <span>
                      <UserOutlined />
                      Personal Profile
                    </span>
                  ),
                  children: (
                    <div className="tab-content">
                      <Row gutter={[24, 24]}>
                        <Col xs={24} lg={16}>
                          <Card title="Personal Information" className="settings-card">
                            <Form
                              form={profileForm}
                              layout="vertical"
                              requiredMark="optional"
                              onFinish={handleProfileUpdate}
                            >
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item
                                    label="Full Name"
                                    name="name"
                                    rules={[{ required: true, message: 'Please enter your name' }]}
                                  >
                                    <Input placeholder="Enter your full name" />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    label="Email"
                                    name="email"
                                    rules={[
                                      { required: true, message: 'Please enter your email' },
                                      { type: 'email', message: 'Please enter a valid email' }
                                    ]}
                                  >
                                    <Input placeholder="Enter your email" />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item
                                    label="Phone Number"
                                    name="phone"
                                  >
                                    <Input placeholder="Enter your phone number" />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    label="Role"
                                    name="role"
                                  >
                                    <Input disabled />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Form.Item>
                                <Button type="primary" htmlType="submit" className="update-profile-btn">
                                  Update Profile
                                </Button>
                              </Form.Item>
                            </Form>
                          </Card>
                        </Col>
                        <Col xs={24} lg={8}>
                          <Card title="Notification Preferences" className="settings-card">
                            <div className="notification-settings">
                              <div className="notification-item">
                                <div>
                                  <Text>Email Notifications</Text>
                                  <div className="notification-item-hint">
                                    <Text type="secondary" style={{ fontSize: 12 }}>Receive email for all notifications shown in the bell</Text>
                                  </div>
                                </div>
                                <Switch
                                  checked={personalProfile?.notifications?.email ?? true}
                                  onChange={(checked) => handleNotificationChange('email', checked)}
                                />
                              </div>
                              <div className="notification-item">
                                <div>
                                  <Text>Push Notifications</Text>
                                  <div className="notification-item-hint">
                                    <Text type="secondary" style={{ fontSize: 12 }}>Coming soon</Text>
                                  </div>
                                </div>
                                <Switch disabled checked={false} />
                              </div>
                              <div className="notification-item">
                                <div>
                                  <Text>SMS Notifications</Text>
                                  <div className="notification-item-hint">
                                    <Text type="secondary" style={{ fontSize: 12 }}>Coming soon</Text>
                                  </div>
                                </div>
                                <Switch disabled checked={false} />
                              </div>
                            </div>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  )
                },
                {
                  key: 'security',
                  label: (
                    <span>
                      <LockOutlined />
                      Security
                    </span>
                  ),
                  children: (
                    <div className="tab-content">
                      <Card title="Change Password" className="settings-card">
                        <Form
                          layout="vertical"
                          requiredMark="optional"
                          onFinish={handlePasswordChange}
                        >
                          <Form.Item
                            label="Current Password"
                            name="currentPassword"
                            rules={[{ required: true, message: 'Please enter your current password' }]}
                          >
                            <Input.Password placeholder="Enter current password" />
                          </Form.Item>
                          <Form.Item
                            label="New Password"
                            name="newPassword"
                            rules={[
                              { required: true, message: 'Please enter a new password' },
                              { min: 8, message: 'Password must be at least 8 characters' }
                            ]}
                          >
                            <Input.Password placeholder="Enter new password" />
                          </Form.Item>
                          <Form.Item
                            label="Confirm New Password"
                            name="confirmPassword"
                            rules={[
                              { required: true, message: 'Please confirm your new password' },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(new Error('Passwords do not match'));
                                },
                              }),
                            ]}
                          >
                            <Input.Password placeholder="Confirm new password" />
                          </Form.Item>
                          <Form.Item>
                            <Button type="primary" htmlType="submit" className="change-password-btn">
                              Change Password
                            </Button>
                          </Form.Item>
                        </Form>
                      </Card>
                    </div>
                  )
                },
                {
                  key: 'team',
                  label: (
                    <span>
                      <TeamOutlined />
                      Team Management
                    </span>
                  ),
                  children: (
                    <div className="tab-content">
                      <div className="team-header">
                        <div className="team-info">
                          <Title level={4} style={{ margin: 0 }}>Team Members</Title>
                          <Text type="secondary">{teamMembers.length} team members</Text>
                        </div>
                        {isSuperAdmin && (
                          <Button
                            type="primary"
                            icon={<UserAddOutlined />}
                            onClick={handleAddUser}
                            className="add-team-member-btn"
                          >
                            Add Team Member
                          </Button>
                        )}
                      </div>
                      <Table
                        columns={teamColumns}
                        dataSource={teamMembers}
                        pagination={false}
                        className="team-table"
                      />
                    </div>
                  )
                },
                {
                  key: 'api-rate-limiting',
                  label: (
                    <span>
                      <ApiOutlined />
                      API Rate Limiting
                    </span>
                  ),
                  children: (
                    <div className="tab-content">
                      <div className="api-rate-limiting-section">
                        <div className="api-rate-limiting-header">
                          <Title level={4} style={{ margin: 0 }}>API Rate Limiting Configuration</Title>
                          <Text type="secondary">
                            Rate limiting controls how many API requests can be made in a given time window (e.g., 60 requests per minute).
                            This helps prevent abuse, ensures fair usage, and protects your backend from overload. Configure limits per endpoint and user type.
                          </Text>
                        </div>
                        <div className="api-rate-limiting-content">
                          <Card className="api-rate-limiting-card">
                            <div className="api-rate-limiting-overview">
                              <Row gutter={[24, 24]}>
                                <Col span={8}>
                                  <div className="overview-item">
                                    <div className="overview-icon">
                                      <ApiOutlined style={{ color: '#324E58' }} />
                                    </div>
                                    <div className="overview-content">
                                      <Text strong>Active Rules</Text>
                                      <Text type="secondary">5 rules configured</Text>
                                    </div>
                                  </div>
                                </Col>
                                <Col span={8}>
                                  <div className="overview-item">
                                    <div className="overview-icon">
                                      <SecurityScanOutlined style={{ color: '#DB8633' }} />
                                    </div>
                                    <div className="overview-content">
                                      <Text strong>System Status</Text>
                                      <Text type="secondary">Rate limiting enabled</Text>
                                    </div>
                                  </div>
                                </Col>
                                <Col span={8}>
                                  <div className="overview-item">
                                    <div className="overview-icon">
                                      <BellOutlined style={{ color: '#DB8633' }} />
                                    </div>
                                    <div className="overview-content">
                                      <Text strong>Recent Alerts</Text>
                                      <Text type="secondary">2 warnings today</Text>
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                            <div className="api-rate-limiting-actions">
                              <Button 
                                type="primary" 
                                icon={<ApiOutlined />}
                                onClick={() => setShowApiRateLimiting(true)}
                              >
                                Configure Rate Limiting
                              </Button>
                              <Button icon={<SecurityScanOutlined />}>
                                View System Status
                              </Button>
                              <Button icon={<BellOutlined />}>
                                Alert History
                              </Button>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                  )
                }
              ]}
            />
              </DashboardSection>
            </div>
          </Spin>
        </Content>
      </Layout>

      {/* Add User Modal */}
      <Modal
        title="Add Team Member"
        open={isAddUserModalVisible}
        onCancel={() => {
          setIsAddUserModalVisible(false);
          addUserForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={addUserForm} layout="vertical" requiredMark="optional" onFinish={handleAddUserSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Full Name"
                name="name"
                rules={[{ required: true, message: 'Please enter full name' }]}
              >
                <Input placeholder="Enter full name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input placeholder="Enter email" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Role"
                name="role"
                rules={[{ required: true, message: 'Please select a role' }]}
              >
                <Select placeholder="Select role">
                  <Option value="Super Admin">Super Admin</Option>
                  <Option value="Admin">Admin</Option>
                  <Option value="Manager">Manager</Option>
                  <Option value="User">User</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="Active">Active</Option>
                  <Option value="Inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Add Member
              </Button>
              <Button onClick={() => setIsAddUserModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit Team Member"
        open={isEditUserModalVisible}
        onCancel={() => {
          setIsEditUserModalVisible(false);
          setEditingUser(null);
        }}
        footer={null}
        width={600}
      >
        {editingUser && (
          <Form form={editUserForm} layout="vertical" requiredMark="optional" onFinish={handleEditUserSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Full Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter full name' }]}
                >
                  <Input placeholder="Enter full name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input placeholder="Enter email" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Role"
                  name="role"
                  rules={[{ required: true, message: 'Please select a role' }]}
                >
                  <Select placeholder="Select role">
                    <Option value="Super Admin">Super Admin</Option>
                    <Option value="Admin">Admin</Option>
                    <Option value="Manager">Manager</Option>
                    <Option value="User">User</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Status"
                  name="status"
                  rules={[{ required: true, message: 'Please select status' }]}
                >
                  <Select placeholder="Select status">
                    <Option value="Active">Active</Option>
                    <Option value="Inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Update Member
                </Button>
                <Button onClick={() => setIsEditUserModalVisible(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Delete Team Member Modal */}
      <Modal
        title="Delete Team Member"
        open={isDeleteUserModalVisible}
        onOk={confirmDeleteUser}
        onCancel={() => {
          setIsDeleteUserModalVisible(false);
          setDeletingUser(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        {deletingUser && (
          <div>
            <p>Are you sure you want to permanently remove this team member?</p>
            <div
              style={{
                padding: '16px',
                background: '#fff7e6',
                borderRadius: '4px',
                marginTop: '16px',
                marginBottom: '16px',
              }}
            >
              <Text strong>Team Member Details:</Text>
              <br />
              <Text>Name: {deletingUser.name}</Text>
              <br />
              <Text>Email: {deletingUser.email}</Text>
              <br />
              <Text>Role: {deletingUser.role}</Text>
            </div>
            <p style={{ color: '#ff4d4f', marginBottom: 0 }}>
              <Text strong>Warning:</Text> This action cannot be undone. They will lose access to the admin panel immediately.
            </p>
          </div>
        )}
      </Modal>

      {/* API Rate Limiting Modal */}
      <Modal
        title="API Rate Limiting Configuration"
        open={showApiRateLimiting}
        onCancel={() => setShowApiRateLimiting(false)}
        footer={null}
        width={1200}
        className="api-rate-limiting-modal"
      >
        <ApiRateLimiting />
      </Modal>
    </Layout>
  );
};

export default Settings; 